import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap, map, catchError, of } from 'rxjs';
import { VideoData } from '../../shared/interfaces';

@Injectable({
  providedIn: 'root'
})
export class YoutubeService {
  private readonly baseUrl = 'https://www.googleapis.com/youtube/v3';
  private readonly apiKey = 'AIzaSyBMxW-yDQnhXoZy7tsKocBvS_OwI6jRT9Y';
  private readonly playlistId = 'PLCe-vj-qYIxgP76B-Ngitawd-U28cmRIh';

  private readonly videosSource = new BehaviorSubject<VideoData[] | null>(null);
  videos$ = this.videosSource.asObservable();

  constructor(private readonly http: HttpClient) {}

  updateVideosList(): void {
    this.getPlaylistItems()
      .pipe(
        map((playlistResponse) => {
          const videoIds = playlistResponse.items.map((item: any) => item.snippet.resourceId.videoId).join(',');
          return videoIds;
        }),
        switchMap((videoIds) => this.getVideosDetails(videoIds)),
        map((videosResponse) => {
          const videos: VideoData[] = videosResponse.items.map((item: any) => ({
            videoId: item.id,
            title: item.snippet.title,
            description: item.snippet.description,
            thumbnail: item.snippet.thumbnails.medium.url,
            duration: this.parseISODuration(item.contentDetails.duration),
            publishedAt: item.snippet.publishedAt,
            tags: item.snippet.tags ?? [],
            isNew: this.isNewVideo(item.snippet.publishedAt)
          }));
          return videos;
        }),
        catchError((error) => {
          return of([] as VideoData[]);
        })
      )
      .subscribe((videos) => {
        this.videosSource.next(videos);
      });
  }

  private getPlaylistItems(): Observable<any> {
    const playlistParams = new HttpParams().set('part', 'snippet').set('maxResults', '50').set('playlistId', this.playlistId).set('key', this.apiKey);

    return this.http.get<any>(`${this.baseUrl}/playlistItems`, { params: playlistParams });
  }

  private getVideosDetails(videoIds: string): Observable<any> {
    const videosParams = new HttpParams().set('part', 'snippet,contentDetails,statistics').set('id', videoIds).set('key', this.apiKey);

    return this.http.get<any>(`${this.baseUrl}/videos`, { params: videosParams });
  }

  private parseISODuration(duration: string): string {
    // Example: PT1H2M10S => 1:02:10
    const isoPattern = /PT(\d+H)?(\d+M)?(\d+S)?/;
    const matches = isoPattern.exec(duration);

    const hours = (matches?.[1] || '0H').replace('H', '') || '0';
    const minutes = (matches?.[2] || '0M').replace('M', '') || '0';
    const seconds = (matches?.[3] || '0S').replace('S', '') || '0';

    const parts = [];
    if (parseInt(hours) > 0) {
      parts.push(hours);
    }
    parts.push(minutes.padStart(2, '0'));
    parts.push(seconds.padStart(2, '0'));

    return parts.join(':');
  }

  private isNewVideo(publishedAt: string): boolean {
    const publishedDate = new Date(publishedAt);
    const now = new Date();
    const diffInDays = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays < 15;
  }

  clearData() {
    this.videosSource.next([]);
  }
}
